/**
 * Centralized content processing utility
 * Handles AI markers and content formatting based on context
 */

// Content processing modes
export const CONTENT_MODES = {
  EDITOR: "editor", // Show AI highlighting in editor
  PREVIEW: "preview", // Clean content for preview modal
  SAVE: "save", // Clean content for saving
  PRINT: "print", // Clean content for printing
  COPY: "copy", // Clean content for copying
};

/**
 * Process content based on the specified mode
 * @param {string} content - The content to process
 * @param {string} mode - The processing mode (CONTENT_MODES)
 * @returns {string} - Processed content
 */
export const processContent = (content, mode = CONTENT_MODES.EDITOR) => {
  // Early return for empty content
  if (!content || typeof content !== "string") return content;

  let processedContent = content;

  // Always remove unwanted asterisks first
  processedContent = removeUnwantedAsterisks(processedContent);

  // Process AI markers based on mode
  switch (mode) {
    case CONTENT_MODES.EDITOR:
      // No special processing in editor mode (AI diff/highlights are applied via htmlDiff).
      break;

    case CONTENT_MODES.PREVIEW:
    case CONTENT_MODES.SAVE:
    case CONTENT_MODES.PRINT:
    case CONTENT_MODES.COPY:
      // Remove AI highlighting for clean output
      processedContent = removeAIHighlighting(processedContent);
      // Soft hyphens (U+00AD) can render as visible syllable breaks in PDF/print
      processedContent = processedContent.replace(/\u00AD/g, "");
      break;

    default:
      console.warn(`Unknown content mode: ${mode}. Using editor mode.`);
  }

  return processedContent;
};

/**
 * Remove AI highlighting from content while preserving the text
 * @param {string} content - The content to clean
 * @returns {string} - Content without AI highlighting
 */
const removeAIHighlighting = (content) => {
  let cleanContent = content;

  // Unwrap AI diff inline spans (ai-inline) while preserving their text/html content
  cleanContent = cleanContent.replace(
    /<span[^>]*class="[^"]*\bai-inline\b[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
    "$1"
  );

  // Remove AI diff block classes (ai-added, ai-modified, ai-removed) and data-ai-diff
  cleanContent = cleanContent.replace(
    /\s*class="([^"]*)"/gi,
    (match, classVal) => {
      const cleaned = classVal
        .split(/\s+/)
        .filter(
          (c) =>
            c &&
            c !== "ai-added" &&
            c !== "ai-modified" &&
            c !== "ai-removed"
        )
        .join(" ");
      return cleaned ? ` class="${cleaned}"` : "";
    }
  );
  cleanContent = cleanContent.replace(/\s+data-ai-diff="[^"]*"/gi, "");

  return cleanContent;
};

/**
 * Remove unwanted asterisks from content
 * @param {string} content - The content to clean
 * @returns {string} - Content without unwanted asterisks
 */
const removeUnwantedAsterisks = (content) => {
  return content
    .replace(/\*\*\*/g, "") // Remove ***
    .replace(/\*\*/g, "") // Remove **
    .replace(/\*/g, ""); // Remove single * (in case there are any)
};

/** Strip invisible / spacer characters from paragraph text checks. */
const INVISIBLE_TEXT_RE = /[\u00A0\u2000-\u200B\u2028\u2029\uFEFF]/g;

/**
 * True when a <p> has no visible text (whitespace, &nbsp;, br only).
 * Matches spacers such as `<p lang="en" dir="ltr">&nbsp;</p>`.
 * @param {HTMLParagraphElement} el
 */
function isEmptyParagraphElement(el) {
  if (!el || el.tagName !== "P") return false;

  const text = (el.textContent || "")
    .replace(INVISIBLE_TEXT_RE, "")
    .replace(/\s+/g, "")
    .trim();
  if (text.length > 0) return false;

  const inner = (el.innerHTML || "")
    .replace(/&nbsp;|&#160;|&#xA0;/gi, "")
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(INVISIBLE_TEXT_RE, "")
    .replace(/\s+/g, "")
    .trim();
  return inner.length === 0;
}

/**
 * Remove spacer paragraphs (including AI/TipTap), e.g.
 * `<p lang="en" dir="ltr">&nbsp;</p>`, `<p></p>`, or empty `<p>` inside lists.
 * @param {string} html
 * @returns {string}
 */
export function stripEmptyParagraphsFromHtml(html) {
  if (!html || typeof html !== "string") return html;
  const trimmed = html.trim();
  if (!trimmed) return html;

  try {
    const doc = new DOMParser().parseFromString(trimmed, "text/html");
    const body = doc.body;
    if (!body) return trimmed;

    for (const p of [...body.querySelectorAll("p")]) {
      if (isEmptyParagraphElement(p)) {
        p.remove();
      }
    }

    return (body.innerHTML || "").trim();
  } catch (_) {
    return trimmed
      .replace(
        /<p\b[^>]*>(?:\s|&nbsp;|&#160;|&#xA0;|\u00A0|<br\s*\/?>)*<\/p>/gi,
        "",
      )
      .trim();
  }
}

/**
 * Get content for editor display (with AI highlighting)
 * @param {string} content - The content to process
 * @returns {string} - Content formatted for editor
 */
export const getEditorContent = (content) => {
  return processContent(content, CONTENT_MODES.EDITOR);
};

/**
 * Get content for preview modal (clean, no highlighting)
 * @param {string} content - The content to process
 * @returns {string} - Clean content for preview
 */
export const getPreviewContent = (content) => {
  return processContent(content, CONTENT_MODES.PREVIEW);
};

/**
 * Get content for saving (clean, no highlighting)
 * @param {string} content - The content to process
 * @returns {string} - Clean content for saving
 */
export const getSaveContent = (content) => {
  return processContent(content, CONTENT_MODES.SAVE);
};

/**
 * Get content for printing (clean, no highlighting)
 * @param {string} content - The content to process
 * @returns {string} - Clean content for printing
 */
export const getPrintContent = (content) => {
  return processContent(content, CONTENT_MODES.PRINT);
};

/**
 * Get content for copying (clean, no highlighting)
 * @param {string} content - The content to process
 * @returns {string} - Clean content for copying
 */
export const getCopyContent = (content) => {
  return processContent(content, CONTENT_MODES.COPY);
};

/**
 * Prefer live TipTap HTML over React `transcript` (may lag behind the editor).
 * @param {{ editor?: { getHTML?: () => string }, transcript?: string }} sources
 */
export function getReportHtmlSource({ editor, transcript } = {}) {
  const live = editor?.getHTML?.();
  if (typeof live === "string" && live.trim()) {
    return live;
  }
  if (typeof transcript === "string" && transcript.trim()) {
    return transcript;
  }
  return "";
}

/**
 * Strip editor-only chrome (drag handles, paragraph wrappers) before export.
 * @param {string} html
 */
export function normalizeReportHtmlForExport(html) {
  if (!html || typeof html !== "string") return html;
  const trimmed = html.trim();
  if (!trimmed) return html;

  try {
    const doc = new DOMParser().parseFromString(trimmed, "text/html");
    const body = doc.body;
    if (!body) return trimmed;

    body
      .querySelectorAll(
        "[data-drag-handle], .draggable-paragraph button, .ProseMirror-trailingBreak",
      )
      .forEach((el) => el.remove());

    body.querySelectorAll(".draggable-paragraph").forEach((wrapper) => {
      const content = wrapper.querySelector(":scope > .draggable-paragraph-content");
      const parent = wrapper.parentNode;
      if (!parent) return;
      const fragment = doc.createDocumentFragment();
      const nodes = content
        ? Array.from(content.childNodes)
        : Array.from(wrapper.childNodes).filter(
            (n) =>
              !(n instanceof HTMLElement) ||
              !n.classList.contains("draggable-paragraph-content"),
          );
      nodes.forEach((n) => fragment.appendChild(n.cloneNode(true)));
      parent.insertBefore(fragment, wrapper);
      wrapper.remove();
    });

    body.querySelectorAll(".draggable-paragraph-content").forEach((el) => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      el.remove();
    });

    body.querySelectorAll("[class]").forEach((el) => {
      el.classList.remove(
        "draggable-paragraph",
        "draggable-paragraph-content",
        "draggable-list-item",
        "group",
      );
      if (!el.classList.length) {
        el.removeAttribute("class");
      }
    });

    return (body.innerHTML || "").trim();
  } catch {
    return trimmed;
  }
}

/**
 * Report body for preview / save / print / copy — live editor + export cleanup.
 * @param {{ editor?: { getHTML?: () => string }, transcript?: string }} sources
 * @param {string} mode - CONTENT_MODES value
 */
export function getReportContentForMode(sources, mode) {
  const raw = getReportHtmlSource(sources);
  if (!raw) return "";

  let processed = processContent(normalizeReportHtmlForExport(raw), mode);

  if (
    mode === CONTENT_MODES.PREVIEW ||
    mode === CONTENT_MODES.SAVE ||
    mode === CONTENT_MODES.PRINT ||
    mode === CONTENT_MODES.COPY
  ) {
    processed = stripEmptyParagraphsFromHtml(processed);
  }

  return processed;
}

export function getPreviewContentFromReport(sources) {
  return getReportContentForMode(sources, CONTENT_MODES.PREVIEW);
}

export function getSaveContentFromReport(sources) {
  return getReportContentForMode(sources, CONTENT_MODES.SAVE);
}

export function getPrintContentFromReport(sources) {
  return getReportContentForMode(sources, CONTENT_MODES.PRINT);
}
